/**
 * TextMessages.js
 * Talkroute
 *
 * Created by Vel Genov on 3/15/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import routes, { routeUrlBuilders } from "@constants/routes";
import { addContactNameToConversation } from "@helpers/functions/contacts";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import {
makeStyles,
Theme
} from "@material-ui/core";
import { ConversationData } from "@services/dataClasses";
import { useGetAllContactsQuery } from "@services/kingcobraApi";
import React,{ useEffect,useMemo,useRef,useState } from "react";
import { useDispatch } from "react-redux";
import { matchPath, useHistory,useParams } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import { Dimmer,Loader } from "semantic-ui-react";
import {
fetchConversations,
selectConversation,
setIsConversationSelected,
setTemporaryConversation,
updateReadStatus
} from "../actions/conversationActions";
import uiString from "../constants/uiString";
import ConversationLayoutManager from "../containers/ConversationLayoutManager";
import validateArray from "../helpers/arrayValidator";
import {
CONTACTS_INITIAL_PAGE,
GET_TEN_THOUSAND_CONTACTS
} from "./Contacts/constants";
import ConversationLineItem from "./conversationComponents/ConversationLineItem";
import MessagesContainer from "./conversationComponents/MessagesContainer";
import ConfirmDialog from "./PresentationComponents/ConfirmDialog";
import apiConstants from "@constants/apiConstants";
import HttpClient from "@services/HttpClient";
import { IConversationV1 } from "@models/Conversation.models";
import { conversationJSONParser, NEW_CONVERSATION_ID, parseConversationToData, WELCOME_CONVERSATION } from "@services/dataClasses/ConversationData";

const menuItems = {
  KEYPAD: "Keypad",
  CALL_HISTORY: "History",
  TEXT_MESSAGES: "Messages",
  TEXT_MESSAGE_DETAILS: "Message Details",
  VOICEMAIL: "Voicemail",
  // this line is a hasty fix due to refresh causing keypad to be selected when voicemails are open
  VOICEMAIL_DETAILS: "Voicemail",
  MEETINGS: "Meetings",
};

const getConversationById = async (conversationId: string) => {
  const url = apiConstants().endpoints.conversations.GET_SINGLE_CONVERSATION;
  const {Get} = HttpClient();
  try {
    const response = await Get<IConversationV1>({
      url,
      id: conversationId
    })
    return response.data
  } catch (error) {
    throw error;
  }
}

const TextMessages = (props) => {
        // get the conversation ID from the URL as a param
        const { convId } = useParams<{ convId: string }>();
        // url params search query
        const query = new URLSearchParams(props.location.search);
        // user ids, allowed to use the virtual phone number (received as url params)
        const allowedUserIds = query.get("userIDs");
        // current logged user's id
        const currentUserId = useAppSelector(
          (state) => state.authentication.userId
        );

        const dispatch = useDispatch();
        const history = useHistory();
        const [open, setOpen] = useState(false);
        const firstRenderFinished = useRef(false);
        const classes = useStyles();

        const _filteredConversations = useAppSelector(
          (state) => state.conversations.filteredData
        );
        const dataState = useAppSelector(
          (state) => state.conversations.dataState
        );
        const currentConversationId = useAppSelector(
          (state) => state.conversations.selectedConversationId
        );
        const conversationDraft = useAppSelector(
          (state) => state.conversations.conversationDraft
        );

        const { data: contactList } = useGetAllContactsQuery({
          params: {
            pageSize: GET_TEN_THOUSAND_CONTACTS,
            page: CONTACTS_INITIAL_PAGE,
          },
        });

        const temporaryConversation = useAppSelector(state => state.conversations.temporaryConversation);
        const parsedConversation = useMemo(() => {
          if (temporaryConversation) {
            if (contactList) {
              return addContactNameToConversation(
                contactList,
                [temporaryConversation]
              ).pop();
            } else {
              return temporaryConversation;
            }
          } else {
            return null;
          }}, [temporaryConversation])

        const filteredConversations = useMemo(
          () =>
            contactList
              ? addContactNameToConversation(
                  contactList,
                  _filteredConversations
                )
              : _filteredConversations,
          [contactList, _filteredConversations]
        );
        
        const conversations = useMemo(() => {
          if (parsedConversation) {
            if (filteredConversations.some(conv => conv.conversationId === parsedConversation.conversationId)) {
              return filteredConversations
            } else {
              return [parsedConversation, ...filteredConversations]
            }
          } else {
            return filteredConversations
          }
        }, [filteredConversations, parsedConversation, temporaryConversation])
        
        useEffect(() => {
          handlePageInit();
          return () => {
            setIsConversationSelected(false);
          };
        }, [conversations, convId]);

        const handlePageInit = () => {
          if (allowedUserIds) {
            const currentUserIsAllowedToAccessConv = allowedUserIds
              .split(",")
              .some((id) => id === String(currentUserId));
            const conversationExists = filteredConversations.some(
              (conv) => conv.conversationId === convId
            );
            if (currentUserIsAllowedToAccessConv) {
              if (conversationExists) {
                dispatch(selectConversation(convId));
                history.push(routeUrlBuilders.OPEN_CONVERSATION(convId));
              } else {
                // Fetch the specific conversation by ID
                _getConversationById(convId).then(() => {
                  history.push(routeUrlBuilders.OPEN_CONVERSATION(convId));
                }).catch(error => {
                  console.error("Error fetching conversation: ", error);
                });
              }
            } else {
              selectFirstConversation();
              setOpen(true);
            }
          } else if (convId) {
            const conversationExists = conversations.some(
              (conv) => conv.conversationId === convId
            );

            !conversationExists && convId !== WELCOME_CONVERSATION && convId !== NEW_CONVERSATION_ID && convId !== "new" && _getConversationById(convId);
            conversationExists && dispatch(selectConversation(convId))
            history.push(routeUrlBuilders.OPEN_CONVERSATION(convId));

          } else if (!currentConversationId) {
            selectFirstConversation();
          } else {
            history.push(routeUrlBuilders.OPEN_CONVERSATION(currentConversationId));
          }
          (fetchConversations as any)();
        };

      const _getConversationById = async (conversationId: string) => {
        try {
          const rawConv = await getConversationById(conversationId);
          if (rawConv.data.length && !rawConv.last_message) {
            rawConv.last_message = rawConv.data[rawConv.data.length - 1];
            rawConv.last_message_at = rawConv.last_message.created_at;
          }

           dispatch(setTemporaryConversation(parseConversationToData(rawConv)));
        } catch (error) { 
           if (!currentConversationId) {
            selectFirstConversation();
          } else {
            history.push(routeUrlBuilders.OPEN_CONVERSATION(currentConversationId));
          }
          throw error;
        }
      }

    const showConversations = (
      filteredConversations: ConversationData[]
    ) => (
    <>
      <CSSTransition
        in
        appear
        key="textmessages"
        timeout={300}
        classNames="fade"
      >
        <ConversationLayoutManager
          showEmptyConversations={showEmptyConversations(
            filteredConversations
          )}
          conversationsList={createConversationLineItems(
            filteredConversations
          )}
        >
          {currentConversationId && (
            <MessagesContainer
              currentConversationId={currentConversationId}
            />
          )}
        </ConversationLayoutManager>
      </CSSTransition>
      <ConfirmDialog
        open={open}
        onCancel={() => setOpen(false)}
        onConfirm={() => setOpen(false)}
        confirmButtonLabel={
          uiString.TEXT_CONVERSATIONS.QUICK_REPLY_FAILED_DIALOG
            .CONFIRM_BUTTON_LABEL
        }
        showCancelButton={false}
        content={
          uiString.TEXT_CONVERSATIONS.QUICK_REPLY_FAILED_DIALOG.BODY
        }
        header={
          uiString.TEXT_CONVERSATIONS.QUICK_REPLY_FAILED_DIALOG.HEADER
        }
      />
    </>
  );

        /**
         * Create each line item for conversations
         *
         * @param conversations an array containing conversations
         */
        const createConversationLineItems = (
          conversations: ConversationData[]
        ) => {
          let returnValue: any = [];
          if (validateArray(conversations)) {
            returnValue = conversations.map((entry) => showLineItems(entry));
          }
          // If conversationDraft is not null, it will display
          // the conversationDraft at beginning of the conversation list
          if (conversationDraft) {
            const { contactNumber } = conversationDraft;
            if (contactNumber) {
              returnValue.unshift(showLineItems(conversationDraft));
            } else {
              returnValue.unshift(showLineItemsNewMessage(conversationDraft));
            }
          }
          return returnValue;
        };

        /**
         * Showing line items for a new conversation draft
         *
         * @param conversationEntry the conversation to display
         *
         * This method is currently not used
         * Keep in place, when we decide to use multiple conversation drafts
         */
        const showLineItemsNewMessage = (conversationEntry) => {
          const { conversationId } = conversationEntry;

          return (
            <ConversationLineItem
              key={conversationId}
              conversationId={conversationId}
              contactNumber={conversationId}
            />
          );
        };

        /**
         * Show the standard line items for text messages
         *
         * @param conversationEntry the conversation to display
         */
        const showLineItems = (conversationEntry: ConversationData) => {
          const { conversationId, lastMessage } = conversationEntry;

          let isRead = true;
          let body = "";

          if (lastMessage !== null) {
            const {
              isRead: isReadLocal,
              body: bodyLocal,
              attachments,
            } = lastMessage;
            isRead = isReadLocal;
            body = bodyLocal;

            if (!body && attachments) {
              body = uiString.PHOTO_ATTACHMENT;
            }
          }

          const messageDateFormatted = conversationEntry.getFormattedDate();
          const contactInformation = conversationEntry.getContactInfo();

          return (
            <ConversationLineItem
              key={conversationId}
              conversationId={conversationId}
              contactNumber={contactInformation}
              lastMessageRead={isRead}
              lastMessageDate={messageDateFormatted}
              lastMessageBody={body}
              markConversationAsReadCallback={markConversationAsRead}
            />
          );
        };

        /**
         * Send an action to mark a conversation as read
         * Currently only marks conversations as read
         *
         * @param conversationId the ID of the conversation to mark
         * @param readStatus the updated read status
         */
        const markConversationAsRead = (props) => {
          const { conversationId, readStatus } = props;
          updateReadStatus({ conversationId, readStatus });
        };

        /**
         * Check if the user's conversations are empty and return a message to be displayed if it they are
         *
         * @param conversations an array containing conversations
         */
        const showEmptyConversations = (conversations) => {
          if (!Array.isArray(conversations) || !(conversations.length > 0)) {
            return (
              <h5 style={{ textAlign: "center", margin: "13px" }}>
                {uiString.EMPTY_CONVERSATIONS}
              </h5>
            );
          }
        };

        /**
         * Displays a lading conversations message
         */
        const showLoadingMessage = () => (
          <Dimmer
            active
            style={{
              height: "100%",
              zIndex: "5",
              backgroundColor: "var(--color-background-black)",
            }}
          >
            <Loader />
          </Dimmer>
        );

        /**
         * Select the 1st conversation, if available
         */
        const selectFirstConversation = () => {
          if (validateArray(conversations)) {
            const firstConversation = conversations[0];

            if (firstConversation) {
              if (currentConversationId) {
                // If a conversation is selected, don't make any changes
                return;
              }

              const { conversationId } = firstConversation;
              dispatch(selectConversation(conversationId));
              history.push(routeUrlBuilders.OPEN_CONVERSATION(conversationId));
            }
          } else {
            dispatch(selectConversation(null));
          }
        };

        return {
          LOADING: showLoadingMessage(),
          ERROR: showConversations(conversations),
          READY: showConversations(conversations),
        }[dataState];
      };;

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    padding: "12px 10px 9px 26px",

    "& h2": {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      fontSize: "13px",
      fontWeight: "bold",
    },
  },
  closeButton: {
    padding: "10px",
    minWidth: "0",
    borderRadius: "6px",
  },
  smallFields: {
    height: "32px",
  },
  formControlWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  divider: {
    margin: "25px 0",
  },
}));

export default TextMessages;
